import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../styles/components/landing/HomePage.css';
import axios from 'axios';

// Import the hero image
import heroImage from '../../../assets/images/homepage_img.png';

// Import device mockup images
import mobilePhoneImage from '../../../assets/images/mobile_image.png';
import laptopImage from '../../../assets/images/laptop_image.png';

// Import feature icons as regular imports (not ReactComponent)
import brainIcon from '../../../assets/icons/landing/brain_icon.png';
import stethoscopeIcon from '../../../assets/icons/landing/docTool_icon.png';
import chartIcon from '../../../assets/icons/landing/chart_icon.png';
import lockIcon from '../../../assets/icons/landing/lock_icon.png';
import calendarIcon from '../../../assets/icons/landing/calendar_icon.png';
import writingIcon from '../../../assets/icons/landing/journal_icon.png';

// Import journey step icons
import checkIcon from '../../../assets/icons/landing/check_icon.png';
import journalStepIcon from '../../../assets/icons/landing/book_icon.png';
import connectIcon from '../../../assets/icons/landing/professional_icon.png';
import verifyIcon from '../../../assets/icons/landing/journal_icon.png';
import manageIcon from '../../../assets/icons/landing/manage_icon.png';
import careIcon from '../../../assets/icons/landing/provideCare_icon.png';

import defaultLogoIcon from '../../../assets/icons/landing/journal_icon.png';

import ClinicModal from './ClinicModal';

const HomePage = () => {
  const navigate = useNavigate();
  const [selectedJourneyType, setSelectedJourneyType] = useState('patients');

  // Enhanced carousel state for smooth scrolling
  const [currentClinicSlide, setCurrentClinicSlide] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const [animationId, setAnimationId] = useState(null);

  // State for partner clinics data
  const [partnerClinicsData, setPartnerClinicsData] = useState([]);
  const [clinicsLoading, setClinicsLoading] = useState(true);

  const [selectedClinic, setSelectedClinic] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Carousel configuration
  const slideWidth = 850; // Fixed slide width in pixels
  const slideGap = 50; // Fixed gap in pixels
  const slideStep = slideWidth + slideGap; // Total step per slide (900px)

  const handleGetStarted = () => {
    navigate('/register');
  };

  // Features data with icons and colors
  const featuresData = [
    {
      id: 1,
      title: "AI-Powered Analysis",
      description: "Journal entries are analyzed using natural language processing to identify mood shifts, emotional patterns, and possible concerns.",
      icon: brainIcon,
    },
    {
      id: 2,
      title: "Professional Network",
      description: "Connect with licensed mental health professionals who use data-driven insights to deliver personalized, effective, and ongoing care.",
      icon: stethoscopeIcon,
    },
    {
      id: 3,
      title: "Progress Tracking",
      description: "Track emotional patterns, symptoms, and mental health progress over time with in-depth analytics and AI-powered insights.",
      icon: chartIcon,
    },
    {
      id: 4,
      title: "Secure Communication",
      description: "Enjoy HIPAA-compliant chat, voice, and video consultations designed for secure, private, and seamless patient-provider interaction.",
      icon: lockIcon,
    },
    {
      id: 5,
      title: "Appointments",
      description: "Easily schedule sessions, manage upcoming appointments, and handle secure payments-all from a single, user-friendly platform.",
      icon: calendarIcon,
    },
    {
      id: 6,
      title: "Free-Flow Journaling",
      description: "Write freely. AI analyzes your entries to reveal emotional patterns and support your mental health professional in tracking progress.",
      icon: writingIcon,
    }
  ];

  // Fetch partner clinics data
  useEffect(() => {
    const fetchPartnerClinics = async () => {
      try {
        setClinicsLoading(true);
        const response = await axios.get('/api/tenants/partner-clinics');
        
        if (response.data.success) {
          setPartnerClinicsData(response.data.data);
        } else {
          console.error('Failed to fetch partner clinics');
          setPartnerClinicsData([]);
        }
      } catch (err) {
        console.error('Error fetching partner clinics:', err);
        setPartnerClinicsData([]);
      } finally {
        setClinicsLoading(false);
      }
    };

    fetchPartnerClinics();
  }, []);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [animationId]);

  // Initialize translate position when data loads
  useEffect(() => {
    if (partnerClinicsData.length > 0) {
      setCurrentTranslate(0);
      setPrevTranslate(0);
      setCurrentClinicSlide(0);
    }
  }, [partnerClinicsData]);

  // Handle logo loading errors
  const handleLogoError = (e) => {
    console.log('Logo failed to load:', e.target.src);
    e.target.src = defaultLogoIcon;
    e.target.onerror = null;
  };

  // Journey data for different user types
  const journeyData = {
    patients: {
      title: "Simplifying Mental Health Care",
      subtitle: "For Every Step of Your Journey",
      steps: [
        {
          id: 1,
          title: "Sign Up & Onboard",
          description: "Create your account, complete your mental health history, and set up your personal profile.",
          icon: checkIcon,
          color: "#4CAF50"
        },
        {
          id: 2,
          title: "Start Journaling",
          description: "Track your mood and journal daily. Our AI gently analyzes patterns and emotional cues over time.",
          icon: journalStepIcon,
          color: "#FF9800"
        },
        {
          id: 3,
          title: "Connect with Professionals",
          description: "Match with verified mental health experts and book sessions tailored to your needs and schedule.",
          icon: connectIcon,
          color: "#2196F3"
        }
      ]
    },
    professionals: {
      title: "Simplifying Mental Health Care",
      subtitle: "For Every Step of Your Journey",
      steps: [
        {
          id: 1,
          title: "Apply & Get Verified",
          description: "Submit your credentials and background. Our team reviews and verifies your professional qualifications.",
          icon: verifyIcon,
          color: "#9C27B0"
        },
        {
          id: 2,
          title: "Manage Patients",
          description: "Access patient insights and review AI-analyzed journals to support informed assessments and care.",
          icon: manageIcon,
          color: "#FF5722"
        },
        {
          id: 3,
          title: "Provide Care",
          description: "Conduct sessions, track progress, manage appointments, and stay connected through secure, HIPAA-compliant communication.",
          icon: careIcon,
          color: "#607D8B"
        }
      ]
    }
  };

  const currentJourney = journeyData[selectedJourneyType];

  // Enhanced drag/touch handlers for smooth scrolling
  const getPositionX = (event) => {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  };

  const handleDragStart = (event) => {
    event.preventDefault();
    setIsDragging(true);
    const posX = getPositionX(event);
    setStartPos(posX);
    setCurrentTranslate(prevTranslate);
    
    // Cancel any ongoing animation
    if (animationId) {
      cancelAnimationFrame(animationId);
      setAnimationId(null);
    }
  };

  const handleDragMove = (event) => {
    if (!isDragging) return;
    
    const currentPosition = getPositionX(event);
    const diff = currentPosition - startPos;
    
    // Convert pixel difference to percentage
    const containerWidth = event.currentTarget.offsetWidth;
    const percentageDiff = (diff / containerWidth) * 100;
    
    // Update translate with smooth movement
    const newTranslate = prevTranslate + percentageDiff;
    
    // Add some resistance at the boundaries
    const maxTranslate = 0;
    const minTranslate = -(partnerClinicsData.length - 1) * slideStep;
    
    if (newTranslate > maxTranslate) {
      // Resistance at the beginning
      setCurrentTranslate(maxTranslate + (newTranslate - maxTranslate) * 0.3);
    } else if (newTranslate < minTranslate) {
      // Resistance at the end
      setCurrentTranslate(minTranslate + (newTranslate - minTranslate) * 0.3);
    } else {
      setCurrentTranslate(newTranslate);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Calculate which slide we should snap to
    const movedBy = currentTranslate - prevTranslate;
    const threshold = 20; // Percentage threshold for slide change
    
    let newSlideIndex = currentClinicSlide;
    
    if (movedBy > threshold && currentClinicSlide > 0) {
      newSlideIndex = currentClinicSlide - 1;
    } else if (movedBy < -threshold && currentClinicSlide < partnerClinicsData.length - 1) {
      newSlideIndex = currentClinicSlide + 1;
    }
    
    // Animate to the target slide
    animateToSlide(newSlideIndex);
  };

  const animateToSlide = (targetSlide) => {
    // Clamp target slide to valid range
    const clampedTarget = Math.max(0, Math.min(targetSlide, partnerClinicsData.length - 1));
    const targetTranslate = -clampedTarget * slideStep;
    
    setCurrentClinicSlide(clampedTarget);
    
    // Smooth animation to target position
    const startTranslate = currentTranslate;
    const distance = targetTranslate - startTranslate;
    const duration = 300; // Animation duration in ms
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const newTranslate = startTranslate + distance * easeOutCubic;
      
      setCurrentTranslate(newTranslate);
      setPrevTranslate(newTranslate);
      
      if (progress < 1) {
        const id = requestAnimationFrame(animate);
        setAnimationId(id);
      } else {
        setAnimationId(null);
      }
    };
    
    const id = requestAnimationFrame(animate);
    setAnimationId(id);
  };

  // Navigation functions
  const nextClinicSlide = () => {
    if (currentClinicSlide < partnerClinicsData.length - 1) {
      animateToSlide(currentClinicSlide + 1);
    }
  };

  const prevClinicSlide = () => {
    if (currentClinicSlide > 0) {
      animateToSlide(currentClinicSlide - 1);
    }
  };

  const goToClinicSlide = (index) => {
    animateToSlide(index);
  };

  const openClinicModal = (clinic) => {
  setSelectedClinic(clinic);
  setIsModalOpen(true);
};

const closeClinicModal = () => {
  setIsModalOpen(false);
  // Small delay before clearing the selected clinic data
  setTimeout(() => {
    setSelectedClinic(null);
  }, 300);
};

  return (
    <div className="homepage-wrapper">
      {/* Hero Section */}
      <section className="homepage-hero-section">
        <div className="homepage-hero-container">
          {/* Left Content */}
          <div className="homepage-hero-content">
            <div className="homepage-hero-badge">
              <span className="homepage-badge-text">NEUROLEX.LIFE</span>
            </div>
            
            <h1 className="homepage-hero-title">
              AI-Powered Depression Assessment, Monitoring & 
              <span className="homepage-title-highlight"> Mental Health Support</span>
            </h1>
            
            <p className="homepage-hero-subtitle">
              Connecting patients with professionals through smart tools and real-time insights.
            </p>
            
            <div className="homepage-hero-actions">
              <button 
                onClick={handleGetStarted}
                className="homepage-cta-button homepage-cta-primary"
              >
                Get Started Now
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4.167 10h11.666M10 4.167L15.833 10 10 15.833" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          
          {/* Right Content - Hero Image with Floating Cards */}
          <div className="homepage-hero-image">
            <div className="homepage-image-container">
              <img 
                src={heroImage} 
                alt="NEUROLEX Mental Health Platform" 
                className="homepage-hero-main-image"
                onError={(e) => {
                  console.log('Image failed to load:', e.target.src);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              
              <div className="homepage-image-placeholder" style={{ display: 'none' }}>
                <div className="homepage-placeholder-content">
                  <div className="homepage-placeholder-icon">
                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                      <rect x="10" y="20" width="60" height="40" rx="8" fill="#4CAF50" fillOpacity="0.1" stroke="#4CAF50" strokeWidth="2"/>
                      <circle cx="25" cy="35" r="3" fill="#4CAF50"/>
                      <rect x="35" y="32" width="20" height="2" fill="#4CAF50"/>
                      <rect x="35" y="38" width="15" height="2" fill="#4CAF50"/>
                    </svg>
                  </div>
                  <div className="homepage-placeholder-text">Image not found</div>
                </div>
              </div>
            </div>
            
            {/* Floating Cards */}
            <div className="homepage-floating-card homepage-floating-card-1">
              <div className="homepage-card-icon homepage-icon-writing">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="homepage-card-content">
                <h4>Start Writing..</h4>
                <p>What do you feel like sharing today?</p>
              </div>
            </div>
            
            <div className="homepage-floating-card homepage-floating-card-2">
              <div className="homepage-card-icon homepage-icon-mood">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2"/>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 9h.01M15 9h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="homepage-card-content">
                <h4>Check In Now</h4>
                <p>Take a moment-how are you right now?</p>
              </div>
            </div>
            
            <div className="homepage-floating-card homepage-floating-card-3">
              <div className="homepage-card-icon homepage-icon-session">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="white" strokeWidth="2"/>
                  <line x1="16" y1="2" x2="16" y2="6" stroke="white" strokeWidth="2"/>
                  <line x1="8" y1="2" x2="8" y2="6" stroke="white" strokeWidth="2"/>
                  <line x1="3" y1="10" x2="21" y2="10" stroke="white" strokeWidth="2"/>
                </svg>
              </div>
              <div className="homepage-card-content">
                <h4>Book a Session</h4>
                <p>Schedule support when you're ready.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="homepage-features-section">
        <div className="homepage-features-container">
          <div className="homepage-features-header">
            <h2 className="homepage-features-title">
              AI-Driven Mental Health Solutions
            </h2>
            <p className="homepage-features-subtitle">
              Empowering patients and professionals with intelligent tools for personalized care and improved outcomes.
            </p>
          </div>

          <div className="homepage-features-grid">
            {featuresData.map((feature) => (
              <div key={feature.id} className="homepage-features-card">
                <div className="homepage-features-card-icon">
                  <img 
                    src={feature.icon} 
                    alt={feature.title}
                    className={`homepage-features-svg-icon ${feature.colorClass}`}
                  />
                </div>
                <div className="homepage-features-card-content">
                  <h3 className="homepage-features-card-title">
                    {feature.title}
                  </h3>
                  <p className="homepage-features-card-description">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="homepage-journey-section">
        <div className="homepage-journey-container">
          <div className="homepage-journey-header">
            <h2 className="homepage-journey-title">
              {currentJourney.title}
            </h2>
            <p className="homepage-journey-subtitle">
              {currentJourney.subtitle}
            </p>
            
            <div className="homepage-journey-dropdown-container">
              <select 
                value={selectedJourneyType}
                onChange={(e) => setSelectedJourneyType(e.target.value)}
                className="homepage-journey-dropdown"
              >
                <option value="patients">For Patients</option>
                <option value="professionals">For Mental Health Professionals</option>
              </select>
              <div className="homepage-journey-dropdown-arrow">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          <div className="homepage-journey-steps">
            {currentJourney.steps.map((step, index) => (
              <div key={step.id} className="homepage-journey-step">
                <div className="homepage-journey-step-card">
                  <div className="homepage-journey-step-icon" style={{ backgroundColor: step.color }}>
                    <img 
                      src={step.icon} 
                      alt={step.title}
                      className="homepage-journey-step-icon-img"
                    />
                  </div>
                  <div className="homepage-journey-step-content">
                    <h3 className="homepage-journey-step-title">{step.title}</h3>
                    <p className="homepage-journey-step-description">{step.description}</p>
                  </div>
                </div>
                {index < currentJourney.steps.length - 1 && (
                  <div className="homepage-journey-connector">
                    <div className="homepage-journey-line"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Clinics Section */}
      <section className="homepage-clinics-section">
        <div className="homepage-clinics-container">
          <div className="homepage-clinics-header">
            <h2 className="homepage-clinics-title">Our Partner Clinics</h2>
            <p className="homepage-clinics-subtitle">
              Trusted healthcare organizations providing exceptional mental health care through Neurolex
            </p>
          </div>

          {clinicsLoading ? (
            <div className="homepage-clinics-loading">
              <p>Loading partner clinics...</p>
            </div>
          ) : partnerClinicsData.length === 0 ? (
            <div className="homepage-clinics-empty">
              <p>No partner clinics available at the moment.</p>
            </div>
          ) : (
            <>
              <div className="homepage-clinics-carousel">
                <div className="homepage-clinics-carousel-container">
                  <div 
                    className="homepage-clinics-slides-wrapper"
                    onMouseDown={handleDragStart}
                    onMouseMove={handleDragMove}
                    onMouseUp={handleDragEnd}
                    onMouseLeave={handleDragEnd}
                    onTouchStart={handleDragStart}
                    onTouchMove={handleDragMove}
                    onTouchEnd={handleDragEnd}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                  >
                    <div 
                      className={`homepage-clinics-slides ${isDragging ? 'dragging' : ''}`}
                      style={{
                        transform: `translateX(${currentTranslate}px)`, // Using pixels instead of percentage
                        transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                      }}
                    >
                      {partnerClinicsData.map((clinic, index) => (
                        <div 
                          key={clinic.id} 
                          className={`homepage-clinics-slide ${
                            index === currentClinicSlide ? 'slide-active' :
                            Math.abs(index - currentClinicSlide) === 1 ? 'slide-adjacent' :
                            'slide-far'
                          }`}
                        >
                          <div className="homepage-clinics-card">
                            <div className="homepage-clinics-logo">
                              <img 
                                src={clinic.logo} 
                                alt={`${clinic.name} logo`}
                                className="homepage-clinics-logo-img"
                                draggable={false}
                                onError={handleLogoError}
                              />
                            </div>

                            <div className="homepage-clinics-content">
                              <h3 className="homepage-clinics-name">{clinic.name}</h3>
                              <p className="homepage-clinics-subtitle-text">{clinic.subtitle}</p>
                              <p className="homepage-clinics-description">{clinic.fullDescription || clinic.description}</p>

                              {clinic.services && (
                                <div className="homepage-clinics-services">
                                  {clinic.services.map((service, serviceIndex) => (
                                    <div key={serviceIndex} className="homepage-clinics-service">
                                      <div className="homepage-clinics-service-icon">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                          <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                      </div>
                                      <span className="homepage-clinics-service-text">{service}</span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <button 
                                className="homepage-clinics-learn-more" 
                                onClick={() => openClinicModal(clinic)}
                              >
                                Learn More
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="homepage-clinics-indicators">
                  {partnerClinicsData.map((_, index) => (
                    <button
                      key={index}
                      className={`homepage-clinics-indicator ${
                        index === currentClinicSlide ? 'active' : ''
                      }`}
                      onClick={() => goToClinicSlide(index)}
                      aria-label={`Go to clinic ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Access Section */}
      <section className="homepage-access-section">
        <div className="homepage-access-container">
          <div className="homepage-access-header">
            <h2 className="homepage-access-title">Access Neurolex Anywhere</h2>
            <p className="homepage-access-subtitle">
              Designed for seamless use across all your devices
            </p>
          </div>

          <div className="homepage-access-content">
            <div className="homepage-access-app mobile-app">
              <div className="homepage-access-device">
                <div className="homepage-access-device-content">
                  <img 
                    src={mobilePhoneImage} 
                    alt="Mobile Application Interface"
                    className="homepage-mobile-mockup-img"
                    onError={(e) => {
                      console.log('Mobile image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="homepage-mobile-placeholder" style={{ display: 'none' }}>
                    <div className="homepage-mobile-placeholder-content">
                      <div className="homepage-mobile-placeholder-icon">📱</div>
                      <span>Mobile App Preview</span>
                    </div>
                  </div>
                </div>
                
                <div className="homepage-access-info">
                  <h3 className="homepage-access-app-title">Mobile Application</h3>
                  <p className="homepage-access-app-subtitle">For Patients</p>
                  <button 
                    className="homepage-access-button primary"
                    onClick={() => window.open('https://drive.google.com/drive/folders/1au0Rnk7vAhx5Q1RGvsqmfnb3ZtP-wJ7Y?usp=sharing', '_blank')}
                  >
                    <span>📱</span>
                    Download APK
                  </button>
                </div>
              </div>
            </div>

            <div className="homepage-access-app web-app">
              <div className="homepage-access-device">
                <div className="homepage-access-device-content">
                  <img 
                    src={laptopImage} 
                    alt="Web Application Dashboard"
                    className="homepage-laptop-mockup-img"
                    onError={(e) => {
                      console.log('Laptop image failed to load:', e.target.src);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="homepage-laptop-placeholder" style={{ display: 'none' }}>
                    <div className="homepage-laptop-placeholder-content">
                      <div className="homepage-laptop-placeholder-icon">💻</div>
                      <span>Web Dashboard Preview</span>
                    </div>
                  </div>
                </div>
                
                <div className="homepage-access-info">
                  <h3 className="homepage-access-app-title">Web Application</h3>
                  <p className="homepage-access-app-subtitle">For Mental Health Professionals and Administrators</p>
                  <div className="homepage-access-buttons">
                    <button 
                      className="homepage-access-button secondary"
                      onClick={() => navigate('/login')}
                    >
                      For Doctors
                    </button>
                    <button 
                      className="homepage-access-button secondary"
                      onClick={() => navigate('/admin/login')}
                    >
                      For Admins
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
     {/* Clinic Modal */}
      {selectedClinic && (
        <ClinicModal
          clinic={selectedClinic}
          isOpen={isModalOpen}
          onClose={closeClinicModal}
        />
      )}               
    </div>
  );
};

export default HomePage;